import type { InfiniteData } from "@tanstack/react-query";
import type { TSearchApprovalAlgorithmsResponse, TWorkflowSortField } from "api/approvalAlgorithms";
import type { ApprovalFlowModel } from "models/ApprovalFlowModel";
import type { ISectionedFilters } from "types/filters";
import type { TSortOrder } from "types/pagination";

import { infiniteQueryOptions, keepPreviousData, queryOptions } from "@tanstack/react-query";
import { getApprovalAlgorithmFilterOptions, searchApprovalAlgorithms } from "api/approvalAlgorithms";
import { Map } from "immutable";
import { sectionedFiltersToBody } from "utils/api/urlSearchParams";

const PER_PAGE = 50;

export const WORKFLOWS_FILTER_OPTIONS_QUERY_KEY = "workflowsFilterOptions";
export const WORKFLOWS_QUERY_KEY = "workflowsList";

export type TWorkflowsQueryOptions = {
  filters: ISectionedFilters;
  order: TSortOrder;
  search: string;
  sortFields: TWorkflowSortField[];
};

const selectWorkflows = (data: InfiniteData<TSearchApprovalAlgorithmsResponse, number>) => ({
  approvalFlows: data.pages.reduce(
    (merged, page) => merged.merge(page.approvalFlows),
    Map<string, ApprovalFlowModel>(),
  ),
  rows: data.pages.flatMap((page) => page.result.toArray()),
  totalCount: data.pages[0]?.pagination.totalResults ?? 0,
});

export const workflowsFilterOptionsQuery = () =>
  queryOptions({
    queryFn: () => getApprovalAlgorithmFilterOptions(),
    queryKey: [WORKFLOWS_FILTER_OPTIONS_QUERY_KEY] as const,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });

export const workflowsQuery = ({ filters, order, search, sortFields }: TWorkflowsQueryOptions) =>
  infiniteQueryOptions({
    queryFn: ({ pageParam, signal }) =>
      searchApprovalAlgorithms({ filters, order, page: pageParam, perPage: PER_PAGE, search, sortFields }, signal),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.pagination.page < lastPage.pagination.totalPages ? lastPage.pagination.page + 1 : undefined,
    placeholderData: keepPreviousData,
    queryKey: [WORKFLOWS_QUERY_KEY, { filters: sectionedFiltersToBody(filters, false), order, search, sortFields }] as const,
    refetchOnWindowFocus: false,
    select: selectWorkflows,
    structuralSharing: false,
  });
